/**
 * Browser file-download helpers for exported template files.
 * No download helper existed in the app before; these are used by the template export handlers.
 */

/**
 * Trigger a browser download for an in-memory blob.
 * Builds an object URL, clicks a temporary anchor, then revokes the URL.
 * No-op outside the browser (SSR guard).
 */
export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();

  // Defer cleanup so the browser can initiate the download before the object URL is revoked;
  // revoking synchronously right after click() can cancel the download in some browsers.
  setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Build a safe, timestamped export filename, mirroring the backend naming
 * (`{level}_{safeName}_{yyyyMMddHHmmss}.uwft.gz`). Built client-side so we don't
 * depend on the cross-origin Content-Disposition header being exposed.
 */
export function buildTemplateFileName(
  level: string,
  name: string | null | undefined,
  now: Date = new Date()
): string {
  const safe = (name || 'template').replace(/[^a-zA-Z0-9]/g, '_') || 'template';
  const pad = (n: number) => `${n}`.padStart(2, '0');
  const ts =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `${level}_${safe}_${ts}.uwft.gz`;
}

/**
 * Extract a human-readable message from an HTTP error whose body is a Blob
 * (the case when the request used responseType:'blob', e.g. export). Falls back
 * to the error's own message, then to `fallback`.
 */
export async function extractBlobErrorMessage(
  error: unknown,
  fallback = 'The export could not be completed.'
): Promise<string> {
  const body = (error as { error?: unknown } | null)?.error;

  if (body instanceof Blob) {
    try {
      const text = await body.text();
      if (!text) return fallback;
      try {
        return messageFromErrorJson(JSON.parse(text)) ?? text;
      } catch {
        return text;
      }
    } catch {
      return fallback;
    }
  }

  return (error as { message?: string } | null)?.message || fallback;
}

/** Tolerantly pull a message out of the backend ErrorResponse shape ({ errors: [{ messages: [] }] }). */
function messageFromErrorJson(json: unknown): string | null {
  const errors = (json as { errors?: { messages?: string[] }[] } | null)
    ?.errors;
  if (Array.isArray(errors)) {
    const messages = errors.flatMap(e => e?.messages ?? []).filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  const message = (json as { message?: string } | null)?.message;
  return message ?? null;
}
